import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useOffline } from './OfflineContext';
import { getQueue, markInFlight, markFailed, markPending, remove, retryAllFailed } from '../utils/syncQueue';
import { moveProduct } from '../services/api';
import { toast } from 'sonner';

/**
 * SyncContext — drains the offline mutation queue whenever the device
 * comes back online.
 *
 * Replay rules:
 *   - FIFO, sequential. Never replay two items in parallel — a stock
 *     transfer's success may depend on the previous one's stock update.
 *   - Each replayed POST carries `X-Client-Action-Id = item.id` so the
 *     backend's `synced_actions` dedup table swallows duplicates
 *     (e.g. when the network blips mid-POST and we retry the same item).
 *   - On network error → item stays `pending` and we abort the drain;
 *     next online tick we'll try again.
 *   - On 4xx → item marked `failed` with the server message, surfaced
 *     in the SyncStatusBadge so the user can choose to retry or drop.
 *
 * Phase 2A scope: only `product_move` items are recognised. Unknown
 * kinds are skipped (left pending) so a future Phase 2B/2C addition
 * to the queue doesn't get silently dropped by an old build of the app.
 */
const SyncContext = createContext({
  queue: [],
  pendingCount: 0,
  failedCount: 0,
  syncing: false,
  syncNow: () => {},
  refreshQueue: () => {},
  retryFailed: () => {},
});

const HANDLERS = {
  product_move: async (item) => {
    const { product_id, to_location_id, quantity, notes, serial_numbers } = item.payload;
    return moveProduct(product_id, to_location_id, quantity, notes || '', serial_numbers || [], item.id);
  },
};

export const SyncProvider = ({ children }) => {
  const { isOnline } = useOffline();
  const [queue, setQueue] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const drainLock = useRef(false);

  const refreshQueue = useCallback(async () => {
    setQueue(await getQueue());
  }, []);

  const drain = useCallback(async () => {
    if (drainLock.current) return;
    drainLock.current = true;
    setSyncing(true);
    try {
      let successCount = 0;
      // Reload fresh on every iteration so newly-enqueued items in the
      // same online session also get drained without restarting drain().
      while (true) {
        const items = await getQueue();
        const next = items.find((i) => i.status === 'pending');
        if (!next) break;
        const handler = HANDLERS[next.kind];
        if (!handler) {
          // Unknown kind from a future version. Mark failed so it
          // doesn't block the rest of the queue and surfaces to the user.
          await markFailed(next.id, `Unknown action kind: ${next.kind}`);
          continue;
        }
        await markInFlight(next.id);
        try {
          await handler(next);
          await remove(next.id);
          successCount += 1;
        } catch (e) {
          const msg = e?.message || 'Sync failed';
          // Network failure → leave as pending for a later retry. We
          // detect this by the absence of a server-issued message
          // (TypeError: Failed to fetch).
          const isNetwork = /failed to fetch|networkerror|load failed/i.test(msg);
          if (isNetwork) {
            // Revert in_flight → pending and stop draining; we'll retry
            // on the next online tick.
            await markPending(next.id);
            break;
          }
          await markFailed(next.id, msg);
        }
      }
      if (successCount > 0) {
        toast.success(`Synced ${successCount} pending ${successCount === 1 ? 'change' : 'changes'}`);
        try {
          window.dispatchEvent(new CustomEvent('tsp:sync-completed', { detail: { count: successCount } }));
        } catch (_) { /* ignore */ }
      }
    } finally {
      setSyncing(false);
      drainLock.current = false;
      await refreshQueue();
    }
  }, [refreshQueue]);

  const syncNow = useCallback(async () => {
    if (!isOnline) {
      toast.error('Still offline — please try again when reconnected');
      return;
    }
    await drain();
  }, [isOnline, drain]);

  const retryFailed = useCallback(async () => {
    await retryAllFailed();
    await refreshQueue();
    if (isOnline) await drain();
  }, [refreshQueue, isOnline, drain]);

  // Auto-refresh queue on mount and whenever an item is enqueued elsewhere
  useEffect(() => {
    refreshQueue();
    const onEnqueued = () => refreshQueue();
    window.addEventListener('tsp:queue-updated', onEnqueued);
    return () => window.removeEventListener('tsp:queue-updated', onEnqueued);
  }, [refreshQueue]);

  // Auto-drain when we transition offline → online.
  useEffect(() => {
    if (isOnline) {
      // Small delay to let the network settle (avoid hammering the
      // server as the reconnect handshake completes).
      const t = setTimeout(() => { drain(); }, 800);
      return () => clearTimeout(t);
    }
  }, [isOnline, drain]);

  const pendingCount = queue.filter((i) => i.status !== 'failed').length;
  const failedCount = queue.filter((i) => i.status === 'failed').length;

  return (
    <SyncContext.Provider value={{
      queue, pendingCount, failedCount, syncing, syncNow, refreshQueue, retryFailed,
    }}>
      {children}
    </SyncContext.Provider>
  );
};

export const useSync = () => useContext(SyncContext);
